// ============================================================================
// GroupCard - one group as a picture card (lobby, carousel, home shelf)
// ============================================================================
import React from 'react';
import { Box, HStack, Text, Avatar, AvatarGroup, Button } from '@chakra-ui/react';
import { Globe, Lock, Users } from 'lucide-react';
import GroupCover from './GroupCover';

export interface GroupCardData {
  id: string; name: string; description?: string; memberCount: number;
  isPublic?: boolean; isOwner?: boolean; isModerator?: boolean; isMember?: boolean; isCreatorGroup?: boolean;
  pendingCount?: number; unreadChat?: boolean; lastMessagePreview?: string | null;
  coverUrl?: string | null; coverTheme?: number | null;
  memberPreview?: Array<{ uid: string; name: string; photoURL: string | null }>;
}

interface Props {
  group: GroupCardData;
  onOpen: (id: string) => void;
  onJoin?: (id: string) => void;
  joining?: boolean;
  /** Full-width card for the carousel */
  wide?: boolean;
}

const GroupCard: React.FC<Props> = ({ group: g, onOpen, onJoin, joining, wide }) => {
  const preview = g.memberPreview || [];
  return (
    <Box
      role="group" cursor="pointer" onClick={() => onOpen(g.id)}
      bg="#0f1722" border="1px solid rgba(139,92,246,0.28)" borderRadius="18px" overflow="hidden"
      transition="transform 0.15s ease, border-color 0.15s ease"
      _hover={{ transform: 'translateY(-2px)', borderColor: 'rgba(139,92,246,0.7)' }}
      minW={wide ? undefined : '260px'} w="100%"
    >
      <GroupCover coverUrl={g.coverUrl} coverTheme={g.coverTheme} name={g.name} height={wide ? 170 : 130} radius="0">
        <Box position="absolute" top="10px" left="12px" right="12px" display="flex" justifyContent="space-between" alignItems="center">
          <HStack spacing={2}>
            {g.isCreatorGroup && <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#FFD700" bg="rgba(0,0,0,0.45)" px={2} py={0.5} borderRadius="full">CREATOR CREW</Text>}
            {g.isOwner && <Text fontSize="10px" fontWeight="800" color="#f0b429" bg="rgba(0,0,0,0.45)" px={2} py={0.5} borderRadius="full">OWNER</Text>}
            {g.isModerator && <Text fontSize="10px" fontWeight="800" color="#c4b5fd" bg="rgba(0,0,0,0.45)" px={2} py={0.5} borderRadius="full">MOD</Text>}
          </HStack>
          <Box color="white" opacity={0.85} bg="rgba(0,0,0,0.45)" borderRadius="full" p={1.5}>
            {g.isPublic === false ? <Lock size={13} /> : <Globe size={13} />}
          </Box>
        </Box>
        <Box position="absolute" left="12px" right="12px" bottom="10px">
          <HStack justify="space-between" align="flex-end">
            <Box minW={0}>
              <HStack spacing={2}>
                <Text color="white" fontWeight="800" fontSize={wide ? 'lg' : 'md'} noOfLines={1} textShadow="0 1px 6px rgba(0,0,0,0.7)">{g.name}</Text>
                {g.unreadChat && <Box w="8px" h="8px" borderRadius="full" bg="#22d3ee" flexShrink={0} />}
              </HStack>
              <HStack spacing={1} color="rgba(255,255,255,0.8)" fontSize="xs">
                <Users size={12} />
                <Text>{g.memberCount} member{g.memberCount === 1 ? '' : 's'}</Text>
                {(g.pendingCount || 0) > 0 && <Text color="#f0b429" fontWeight="700">· {g.pendingCount} request{g.pendingCount === 1 ? '' : 's'}</Text>}
              </HStack>
            </Box>
            {preview.length > 0 && (
              <AvatarGroup size="sm" max={3} spacing="-10px">
                {preview.map((m) => <Avatar key={m.uid} name={m.name} src={m.photoURL || undefined} border="2px solid #0f1722" />)}
              </AvatarGroup>
            )}
          </HStack>
        </Box>
      </GroupCover>
      <Box px={3} py={2.5}>
        <Text color={g.unreadChat ? 'cyan.200' : 'gray.400'} fontSize="sm" noOfLines={2} minH="20px">
          {g.unreadChat && g.lastMessagePreview ? g.lastMessagePreview : (g.description || 'No description yet.')}
        </Text>
        {onJoin && !g.isMember && (
          <Button mt={2} size="sm" w="100%" colorScheme="purple" isLoading={joining}
            onClick={(e) => { e.stopPropagation(); onJoin(g.id); }}>
            {g.isPublic === false ? 'Request to join' : 'Join group'}
          </Button>
        )}
        {onJoin && g.isMember && (
          <Button mt={2} size="sm" w="100%" variant="outline" colorScheme="cyan" onClick={(e) => { e.stopPropagation(); onOpen(g.id); }}>
            Open
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default GroupCard;
